import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE } from '../utils/api';

// Define types
interface KycApplication {
  id: number;
  userId: string;
  firstName: string;
  lastName: string;
  gender: string;
  dob: string;
  address: string;
  city: string;
  country: string;
  documentType: string;
  idFrontPath?: string;
  idBackPath?: string;
  passportPath?: string;
  status: 'Pending' | 'Verified' | 'Rejected';
  rejectionReason?: string;
  createdAt: string;
}

const Kyc: React.FC = () => {
  const [applications, setApplications] = useState<KycApplication[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedApp, setSelectedApp] = useState<KycApplication | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [filter, setFilter] = useState<'All' | 'Pending' | 'Verified' | 'Rejected'>('Pending');

  const apiBaseUrl = `${BASE}/kyc`;

  // Fetch all KYC applications
  useEffect(() => {
    const fetchApplications = async () => {
      setLoading(true);
      try {
        // This endpoint would need to be added to your API
        const response = await axios.get(`${apiBaseUrl}/admin/applications`);
        setApplications(response.data);
      } catch (error) {
        console.error('Failed to fetch KYC applications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
    
    // Refresh data every 2 minutes
    const interval = setInterval(fetchApplications, 120000);
    return () => clearInterval(interval);
  }, []);

  // Filter applications based on status
  const filteredApplications = applications.filter(app => 
    filter === 'All' ? true : app.status === filter
  );

  // Update KYC status - Fixed to ensure type safety
  const updateStatus = async (userId: string, status: 'Verified' | 'Rejected') => {
    try {
      await axios.put(`${apiBaseUrl}/update-status`, {
        userId,
        status,
        rejectionReason: status === 'Rejected' ? rejectionReason : undefined
      });
      
      // Update local state with fixed type safety
      setApplications(prevApps => 
        prevApps.map(app => {
          if (app.userId === userId) {
            return {
              ...app,
              status,
              rejectionReason: status === 'Rejected' ? rejectionReason : undefined
            };
          }
          return app;
        })
      );
      
      // Clear selected application and rejection reason
      setSelectedApp(null);
      setRejectionReason('');
      
    } catch (error) {
      console.error('Failed to update KYC status:', error);
    }
  };
  const getFilenameFromPath = (path: string | undefined): string | undefined => {
    if (!path) return undefined;
    // Extract just the filename from the path
    const parts = path.split('/');
    return parts[parts.length - 1];
  };

  // Get document URL for display - Fixed return type to string | undefined
  const getDocumentUrl = (path: string | undefined): string | undefined => {
    if (!path) return undefined;
    
    // Extract the filename from the path
    const filename = getFilenameFromPath(path);
    if (!filename) return undefined;
    
    // Add file extension if missing
    const filenameWithExt = filename.includes('.') ? filename : `${filename}.png`;
    
    // Log the URL being generated
    const url = `${apiBaseUrl}/documents/${filenameWithExt}`;
    console.log('Generated document URL:', url);
    
    return url;
  };

  return (
    <div className='ml-0 lg:ml-[220px] p-5 '>
       <div className="flex flex-col min-h-screen bg-[#1a1d1e]">
      {/* Header */}
      <header className="bg-[#25ee89] text-[#1a1d1e] shadow-lg">
        <div className="container mx-auto py-4 px-6">
          <h1 className="text-2xl font-bold">KYC Verification Admin Panel</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto flex flex-col md:flex-row flex-1 p-6 gap-6">
        {/* Left Side - Applications List */}
        <div className="w-full md:w-1/3 bg-[#232626] rounded-lg shadow-md overflow-hidden">
          <div className="p-4 border-b border-[#2f3536]">
            <h2 className="text-lg font-semibold text-[#e2e8f0]">Applications</h2>
            <div className="mt-2 flex gap-2">
              {(['All', 'Pending', 'Verified', 'Rejected'] as const).map((status) => (
                <button
                  key={status}
                  className={`px-3 py-1 rounded-full text-sm ${
                    filter === status
                      ? 'bg-[#25ee89] text-[#1a1d1e]'
                      : 'bg-[#2f3536] text-[#e2e8f0]'
                  }`}
                  onClick={() => setFilter(status)}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
          
          <div className="overflow-y-auto h-96">
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#25ee89]"></div>
              </div>
            ) : filteredApplications.length === 0 ? (
              <div className="p-6 text-center text-[#94a3b8]">
                No {filter !== 'All' ? filter.toLowerCase() : ''} applications found
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {filteredApplications.map((app) => (
                  <li
                    key={app.id}
                    className={`p-4 cursor-pointer hover:bg-[#2f3536] ${
                      selectedApp?.id === app.id ? 'bg-[#2f3536]' : ''
                    }`}
                    onClick={() => setSelectedApp(app)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-[#e2e8f0]">{app.firstName} {app.lastName}</p>
                        <p className="text-sm text-[#94a3b8]">ID: {app.userId}</p>
                      </div>
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          app.status === 'Pending'
                            ? 'bg-[#6b4a0f] text-[#fbbf24]'
                            : app.status === 'Verified'
                            ? 'bg-[#1f3b2a] text-[#25ee89]'
                            : 'bg-[#5a2e2a] text-[#ff6b6b]'
                        }`}
                      >
                        {app.status}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right Side - Application Details */}
        <div className="w-full md:w-2/3 bg-[#232626] rounded-lg shadow-md overflow-hidden">
          {selectedApp ? (
            <div className="h-full flex flex-col">
              <div className="p-4 border-b border-[#2f3536] flex justify-between items-center">
                <h2 className="text-lg font-semibold text-[#e2e8f0]">
                  Application Details - {selectedApp.firstName} {selectedApp.lastName}
                </h2>
                <span
                  className={`px-3 py-1 rounded-full text-sm ${
                    selectedApp.status === 'Pending'
                      ? 'bg-[#6b4a0f] text-[#fbbf24]'
                      : selectedApp.status === 'Verified'
                      ? 'bg-[#1f3b2a] text-[#25ee89]'
                      : 'bg-[#5a2e2a] text-[#ff6b6b]'
                  }`}
                >
                  {selectedApp.status}
                </span>
              </div>

              <div className="p-6 flex-1 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-md font-medium text-[#e2e8f0]">Personal Information</h3>
                    <div className="bg-[#1a1d1e] p-4 rounded-lg space-y-2">
                      <div className="grid grid-cols-2">
                        <span className="text-[#94a3b8]">Full Name</span>
                        <span className="text-[#e2e8f0]">{selectedApp.firstName} {selectedApp.lastName}</span>
                      </div>
                      <div className="grid grid-cols-2">
                        <span className="text-[#94a3b8]">Gender</span>
                        <span className="text-[#e2e8f0]">{selectedApp.gender}</span>
                      </div>
                      <div className="grid grid-cols-2">
                        <span className="text-[#94a3b8]">Date of Birth</span>
                        <span className="text-[#e2e8f0]">{new Date(selectedApp.dob).toLocaleDateString()}</span>
                      </div>
                      <div className="grid grid-cols-2">
                        <span className="text-[#94a3b8]">Address</span>
                        <span className="text-[#e2e8f0]">{selectedApp.address}</span>
                      </div>
                      <div className="grid grid-cols-2">
                        <span className="text-[#94a3b8]">City</span>
                        <span className="text-[#e2e8f0]">{selectedApp.city}</span>
                      </div>
                      <div className="grid grid-cols-2">
                        <span className="text-[#94a3b8]">Country</span>
                        <span className="text-[#e2e8f0]">{selectedApp.country}</span>
                      </div>
                      <div className="grid grid-cols-2">
                        <span className="text-[#94a3b8]">Document Type</span>
                        <span className="text-[#e2e8f0] capitalize">{selectedApp.documentType}</span>
                      </div>
                    </div>
                  </div>

                  {/* Document Verification */}
                  <div className="space-y-4">
                    <h3 className="text-md font-medium text-[#e2e8f0]">Document Verification</h3>
                    <div className="bg-[#1a1d1e] p-4 rounded-lg space-y-4">
                      {selectedApp.documentType === 'idcard' ? (
                        <>
                          <div>
                            <p className="text-[#94a3b8] mb-2">ID Card Front</p>
                            {selectedApp.idFrontPath ? (
                              <img
                                src={getDocumentUrl(selectedApp.idFrontPath)}
                                alt="ID Card Front"
                                className="w-full h-48 object-contain border rounded"
                              />
                            ) : (
                              <div className="border rounded p-4 text-center text-[#94a3b8]">
                                No front ID image found
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="text-[#94a3b8] mb-2">ID Card Back</p>
                            {selectedApp.idBackPath ? (
                              <img
                                src={getDocumentUrl(selectedApp.idBackPath)}
                                alt="ID Card Back"
                                className="w-full h-48 object-contain border rounded"
                              />
                            ) : (
                              <div className="border rounded p-4 text-center text-[#94a3b8]">
                                No back ID image found
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div>
                          <p className="text-[#94a3b8] mb-2">Passport</p>
                          {selectedApp.passportPath ? (
                            <img
                              src={getDocumentUrl(selectedApp.passportPath)}
                              alt="Passport"
                              className="w-full h-48 object-contain border rounded"
                            />
                          ) : (
                            <div className="border rounded p-4 text-center text-[#94a3b8]">
                              No passport image found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Rejection Reason (if applicable) */}
                {selectedApp.status === 'Rejected' && selectedApp.rejectionReason && (
                  <div className="mt-6">
                    <h3 className="text-md font-medium text-[#ff6b6b]">Rejection Reason</h3>
                    <div className="bg-[#5a2e2a] p-4 rounded-lg mt-2 text-[#ff9999]">
                      {selectedApp.rejectionReason}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {selectedApp.status === 'Pending' && (
                <div className="p-4 border-t border-[#2f3536] bg-[#1a1d1e]">
                  <div className="flex flex-col space-y-4">
                    <div>
                      <label htmlFor="rejection-reason" className="block text-sm font-medium text-[#e2e8f0]">
                        Rejection Reason (required if rejecting)
                      </label>
                      <textarea
                        id="rejection-reason"
                        rows={3}
                        className="mt-1 block w-full rounded-md bg-[#1a1d1e] border border-[#2f3536] text-[#e2e8f0] shadow-sm focus:border-[#25ee89] focus:ring-[#25ee89] placeholder-[#94a3b8]"
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Explain why this application is being rejected..."
                      />
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button
                        className="px-4 py-2 bg-[#ff6b6b] text-[#1a1d1e] rounded-md hover:bg-[#ff5252] font-medium disabled:bg-[#994444]"
                        onClick={() => updateStatus(selectedApp.userId, 'Rejected')}
                        disabled={!rejectionReason.trim()}
                      >
                        Reject
                      </button>
                      <button
                        className="px-4 py-2 bg-[#25ee89] text-[#1a1d1e] rounded-md hover:bg-[#1fcc76] font-medium"
                        onClick={() => updateStatus(selectedApp.userId, 'Verified')}
                      >
                        Approve
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="h-full flex justify-center items-center text-[#94a3b8] p-8">
              <div className="text-center">
                <svg
                  className="mx-auto h-12 w-12 text-[#94a3b8]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <h3 className="mt-2 text-lg font-medium text-[#e2e8f0]">No application selected</h3>
                <p className="mt-1 text-[#94a3b8]">Select an application from the list to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
      </div>
   
  );
};

export default Kyc;